import { AssessmentDetailsApiData } from "./types/assessmentDetailsApiData";
import { DisplayData } from "./types/displayData";
import { isNumber } from "src/utils/isNumber";
import AssessmentUtilService from "src/services/assessment/assessmentUtilService";
import { EvaluationStatusEnum } from "./types/evaluationStatusEnum";
import { CustomColorTokens } from "src/constants/customColorTokens";
import { EvaluationDetailsApiData } from "./types/evaluationDetailsApiData";
import { AssessmentVideoTitleFields } from "./types/assessmentVideoTitleFields";
import {getTimeDiffInSeconds} from "src/utils/dateUtil";
import {EvaluationErrorEnum} from "src/services/assessment/types/evaluationErrorEnum";

/**
 * Class responsible for data shown in the Assessment Library page in both
 * mobile and desktop view. In mobile view DisplayData powers the list of cards shown.
 * In desktop view, DisplayData powers the table rows rendered.
 */
class AssessmentDisplayDataService {
  getLastUpdated(assessment: AssessmentDetailsApiData) {
    const NUMBER_OF_MILLISECONDS_IN_SECOND = 1000;
    // This is done until the backend returns a create at date for an assessment which can be
    // used if both evaluations are null
    const DEFAULT_TIMESTAMP_IF_NO_EVALUATION = 1672556407; // evaluated to jan 1st 2023
    let lastUpdated = {
      timestamp: 0,
      utcString: "",
      shortFormatUtcString: ""
    };
    // try to choose date corresponding to follow up evaluation if present
    if (
      assessment.followupEvaluation?.statusUpdateDate !== null &&
      assessment.followupEvaluation?.statusUpdateDate !== undefined
    ) {
      lastUpdated.timestamp = +assessment.followupEvaluation.statusUpdateDate;
    } else {
      lastUpdated.timestamp = +(assessment.initialEvaluation?.statusUpdateDate ?? DEFAULT_TIMESTAMP_IF_NO_EVALUATION);
    }

    const date = new Date(lastUpdated.timestamp * NUMBER_OF_MILLISECONDS_IN_SECOND);
    lastUpdated.utcString = date.toUTCString();
    lastUpdated.shortFormatUtcString = `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
    return lastUpdated;
  }

  calculateRiskReduction(initialRebaValue: number, followUpRebaValue: number): string {
    let riskReductionValue = "";

    if (initialRebaValue !== 0 && followUpRebaValue <= initialRebaValue) {
      let difference = Math.abs(followUpRebaValue - initialRebaValue);
      let percentageRoundedToInteger = Math.round((Math.abs(difference / initialRebaValue) * 100))
      riskReductionValue = `${percentageRoundedToInteger}%`;
    }
    return riskReductionValue;
  }

  getScoreValueOrShowStatus(evaluation?: EvaluationDetailsApiData | null): string {
    const displayMap = new Map();
    displayMap.set(EvaluationStatusEnum.ERROR, "Error");
    displayMap.set(EvaluationStatusEnum.IN_PROGRESS, "Processing");
    displayMap.set(EvaluationStatusEnum.UPLOADING, "Uploading");
    displayMap.set(EvaluationStatusEnum.NOT_STARTED, "");
    if (evaluation === null || evaluation === undefined) {
      return displayMap.get(EvaluationStatusEnum.ERROR);
    }
    if (AssessmentUtilService.isUploadError(evaluation.status, Number(evaluation.statusUpdateDate))) {
      evaluation.errorMessage = EvaluationErrorEnum.UPLOAD_TIMEOUT;
      evaluation.status = EvaluationStatusEnum.ERROR;
      return displayMap.get(EvaluationStatusEnum.ERROR);
    }
    displayMap.set(EvaluationStatusEnum.SUCCESS, evaluation.highestRebaScore + "");

    return displayMap.get(evaluation.status) ?? displayMap.get(EvaluationStatusEnum.ERROR);
  }

  // returns the css class for the video title cell in the assessment library table
  // or in the assessment card
  getVideoTitleClassName(evaluationValue: string): string {
    let videoTitleClassName = "assessment-library-content__video-title";

    if (evaluationValue === "Processing" || evaluationValue === "" || evaluationValue === "Uploading") {
      videoTitleClassName = "assessment-library-content__video-title--processing";
    } else if (evaluationValue === "Error") {
      videoTitleClassName = "assessment-library-content__video-title--error";
    }

    return videoTitleClassName;
  }

  assessmentsApiDataToDisplayData(assessmentData: AssessmentDetailsApiData): DisplayData {
    const initialEvaluationValue = this.getScoreValueOrShowStatus(assessmentData.initialEvaluation);
    const followUpEvaluationValue = this.getScoreValueOrShowStatus(assessmentData.followupEvaluation);

    const initialEvaluationBorderClass = isNumber(initialEvaluationValue)
      ? AssessmentUtilService.getRebaBorderClass(Number(initialEvaluationValue))
      : "";
    const followUpEvaluationBorderClass = isNumber(followUpEvaluationValue)
      ? AssessmentUtilService.getRebaBorderClass(Number(followUpEvaluationValue))
      : "";
    const riskReductionPercentage =
      isNumber(initialEvaluationValue) && isNumber(followUpEvaluationValue)
        ? this.calculateRiskReduction(+initialEvaluationValue, +followUpEvaluationValue)
        : "";
    const videoTitleClassName = this.getVideoTitleClassName(initialEvaluationValue);
    const lastUpdated = this.getLastUpdated(assessmentData);
    const hasError = initialEvaluationValue === "Error" || followUpEvaluationValue === "Error";

    return {
      videoTitle: AssessmentUtilService.generateVideoTitleFromAssessment(assessmentData as AssessmentVideoTitleFields),
      assessmentId: assessmentData.assessmentId,
      initialEvaluation: {
        value: initialEvaluationValue,
        borderClass: initialEvaluationBorderClass,
        evaluationId: assessmentData.initialEvaluation?.evaluationId,
        errorType: assessmentData.initialEvaluation?.errorMessage || "",
      },
      followUpEvaluation: {
        value: followUpEvaluationValue,
        borderClass: followUpEvaluationBorderClass,
        evaluationId: assessmentData.followupEvaluation?.evaluationId,
        errorType: assessmentData.followupEvaluation?.errorMessage || "",
      },
      tool: assessmentData.type,
      site: assessmentData.site,
      processPath: assessmentData.processPath,
      organization: assessmentData.organization,
      siteType: assessmentData.siteType,
      subOrganization: assessmentData.subOrganization,
      region: assessmentData.region,
      country: assessmentData.country,
      riskReductionPercentage: riskReductionPercentage,
      lastUpdatedUtcString: lastUpdated.utcString,
      lastUpdatedTimestamp: lastUpdated.timestamp,
      lastUpdatedShortFormatUtcString: lastUpdated.shortFormatUtcString,
      backgroundColor:
        hasError
          ? CustomColorTokens.BRAND_RED_LIGHT
          : "",
      hasError: hasError,
      videoTitleClassName: videoTitleClassName,
      activity: assessmentData.activity
    };
  }
}

export default new AssessmentDisplayDataService();
